import packageJson from "@/../package.json";

import { Update } from "@/types/updates";
import { useState, useEffect, useCallback } from "react";
import { getCookie, setCookie } from "@/lib/cookies";
import Link from "next/link";
import {
  LIBUM_DOCS_NEXUM_URL,
  LIBUM_DOCS_POWERON_PRO_URL,
  LIBUM_FEEDBACK_URL,
  LIBUM_GLACI_URL,
  LIBUM_POWERON_PIPELINES_MARKETPLACE_URL,
  MARKDOWN_URL,
} from "@/lib/constants";
import Image from "next/image";

const COOKIE_KEY = "update-last-viewed";
const ONE_YEAR_IN_SECONDS = 60 * 60 * 24 * 365;

// Module-level flag to prevent multiple auto-shows in the same page session
let hasAutoShownThisSession = false;

// Reset function for testing
export function resetAutoShowFlag() {
  hasAutoShownThisSession = false;
}

const UPDATES: Update[] = [
  {
    id: packageJson.version,
    releaseDate: new Date(2025, 11, 17),
    title: "",
    image: "/images/whats-new-december-15.webp",
    opener: (
      <>
        <p className="pb-4">
          Many of you have heard that we&apos;re building a creative platform
          for Jack Henry™ credit unions, some of you may have even already seen
          a sneak peak of what it can do at a recent conference.
        </p>
        <p>
          The creative platform is called{" "}
          <Link
            href={LIBUM_GLACI_URL}
            target="_blank"
            className="font-semibold text-brand-accent hover:underline"
          >
            Glaci
          </Link>{" "}
          and what we&apos;re building into it will set your instituiton up for
          the future and make your capabilities feel limitless.
          <span className="inline-flex px-1 align-middle">
            <Image
              src="/emojis/sparkles.svg"
              alt=""
              width={18}
              height={18}
              className="mb-1"
            />
          </span>
        </p>
      </>
    ),
    groups: [
      {
        id: "overlay",
        title: "An overlay for Symitar Quest",
        details: [
          {
            id: "1",
            node: (
              <p>
                <span className="font-bold">Coexist with Quest.</span> Glaci is
                bound on-top of Symitar Quest and we&apos;re aware of every
                button you click & member you serve.
              </p>
            ),
          },
          {
            id: "2",
            node: (
              <p>
                <span className="font-bold">Quick navigation.</span> Switch
                members like never before with a fuzzy-search trigger-able
                through a keyboard shortcut.
              </p>
            ),
          },
        ],
      },
      {
        id: "workflows",
        title: "With workflows, docs, and procedures",
        details: [
          {
            id: "1",
            node: (
              <p>
                <span className="font-bold">Playbooks & Opportunities.</span>{" "}
                Run a series of pre-built or custom components (with our UI-kit)
                that trigger automatically & enforce compliance. Capture leads
                with members as you interact with them (no research required).
              </p>
            ),
          },
          {
            id: "2",
            node: (
              <p>
                <span className="font-bold">Glaci Docs.</span> We&apos;re on
                SMUG. We are familiar with the expressed pain of your existing
                docs solutions. The cure is coming.
                <span className="inline-flex px-1 align-middle">
                  <Image
                    src="/emojis/stethoscope.svg"
                    alt=""
                    width={18}
                    height={18}
                    className="mb-1"
                  />
                </span>
              </p>
            ),
          },
          {
            id: "3",
            node: (
              <p>
                <span className="font-bold">
                  Standard Operating Procedures.
                </span>{" "}
                Put away the training binders. Gain access to{" "}
                <Link
                  href={MARKDOWN_URL}
                  target="_blank"
                  className="font-semibold text-brand-accent hover:underline"
                >
                  Markdown
                </Link>{" "}
                based procedures and training guides in beautiful branded
                typography.
              </p>
            ),
          },
        ],
      },
      {
        id: "teams",
        title: "flexible and additive for your teams",
        details: [
          {
            id: "1",
            node: (
              <p>
                <span className="font-bold">Updates over-the-air.</span> The
                issue with any sort of custom development is that it will
                naturally become technical debt. Stay up-to-date always with an
                ecosystem that&apos;s constantly growing.
              </p>
            ),
          },
          {
            id: "2",
            node: (
              <p>
                <span className="font-bold">Notice a gap?</span> With Glaci,
                we&apos;re in the business of building integration and utility
                on beautiful UIs. If you can&apos;t do something, just tell us
                and we&apos;ll ship it in a future update for your team.
              </p>
            ),
          },
        ],
      },
    ],
    closer: (
      <>
        <p className="pb-4">
          Though we&apos;re in the midst of development, we can only build what
          we know. This is why we&apos;re inviting <strong>five</strong> credit
          unions to partner with us to bring it to maturity.
          <span className="inline-flex px-1 align-middle">
            <Image
              src="/emojis/rocket.svg"
              alt=""
              width={18}
              height={18}
              className="mb-1"
            />
          </span>
        </p>
        <p className="pb-4">
          We have built game changers for the Jack Henry™ credit union
          devlopment community. Help us build out this new frontier for everyone
          else.
        </p>
        <p>
          Reach out to a Founder if you&apos;re in. Come taste the cake.
          <span className="inline-flex px-1 align-middle">
            <Image
              src="/emojis/cake.svg"
              alt=""
              width={18}
              height={18}
              className="mb-1"
            />
          </span>
        </p>
      </>
    ),
  },
  {
    id: "1.1.0",
    releaseDate: new Date(2025, 5, 24),
    title: "",
    image: "/images/whats-new-june-25.jpg",
    opener: (
      <p>
        We&apos;ve got an exciting update to share for organizations with teams
        that develop in PowerOn and have ever wished there was a way to track
        changes to their code.
        <br />
        <br />
        For those of you on Azure DevOps, we just released a new extension that
        brings your organization merge validation for PowerOn and a completely
        managed development lifecycle for Symitar —{" "}
        <Link
          href={LIBUM_POWERON_PIPELINES_MARKETPLACE_URL}
          target="_blank"
          className="font-semibold text-brand-accent hover:underline"
        >
          PowerOn Pipelines
        </Link>
      </p>
    ),
    groups: [
      {
        id: "poweron-pipelines",
        title: "Version control for Symitar",
        details: [
          {
            id: "1",
            node: (
              <p>
                <span className="font-bold">
                  Run validation on updated PowerOn code.
                </span>{" "}
                For every Pull Request (PR) you create, PowerOn Pipelines will
                detect changed PowerOns and validate them against Symitar. This
                ensures the code that lands in production is always valid and
                ready to run.
              </p>
            ),
          },
          {
            id: "2",
            node: (
              <p>
                <span className="font-bold">
                  Safely deploy PowerOn code to target Syms.
                </span>{" "}
                Treat the Syms in your Symitar environment as deployment stages
                and release specific build versions of PowerOn code to them with
                confidence
                <span className="inline-flex px-1 align-middle">
                  <Image
                    src="/emojis/rocket.svg"
                    alt=""
                    width={18}
                    height={18}
                    className="mb-1"
                  />
                </span>
              </p>
            ),
          },
          {
            id: "3",
            node: (
              <p>
                <span className="font-bold">
                  Works for both SSH and HTTPs connections.
                </span>{" "}
                We know that not every credit union still has access to Symitar
                CLI over SSH, so we made sure that PowerOn Pipelines works with
                both SSH and HTTPs connection types.
              </p>
            ),
          },
        ],
      },
    ],
    closer: (
      <>
        <p className="pb-4">
          It&apos;s our mission to bring the greatest software to your credit
          union. To do this, we know we have to listen to the community.
          <span className="inline-flex px-1 align-middle">
            <Image
              src="/emojis/cat-bug-love.webp"
              alt="A cat that loves like a bug"
              height={96}
              width={84}
              style={{ height: "20px", width: "auto" }}
              className="mb-1"
            />
          </span>
        </p>
        <p className="block">
          Let us know what you want on our{" "}
          <Link
            href={LIBUM_FEEDBACK_URL}
            target="_blank"
            className="font-semibold text-brand-accent hover:underline"
          >
            Feedback
          </Link>{" "}
          portal! Maybe we&apos;ll give you a free cookie!
          <span className="inline-flex px-1 align-middle">
            <Image
              src="/emojis/cookie.svg"
              alt=""
              width={18}
              height={18}
              className="mb-1"
            />
          </span>
        </p>
      </>
    ),
  },
  {
    id: "1.0.0",
    releaseDate: new Date(2025, 3, 16),
    title: "",
    image: "/images/whats-new.svg",
    opener: (
      <p>
        At Libum, we&apos;re constantly looking for ways to transform the way
        credit unions interact with their core.
        <br />
        <br />
        First, we brought the community{" "}
        <Link
          href={LIBUM_DOCS_NEXUM_URL}
          target="_blank"
          className="font-semibold text-brand-accent hover:underline"
        >
          NexumAPI
        </Link>{" "}
        as a JSON variant to SymXchange™ with fully fledged docs. Next, we
        supercharged the PowerOn™ language by coupling it with the powerful APIs
        of Visual Studio Code through our extension,{" "}
        <Link
          href={LIBUM_DOCS_POWERON_PRO_URL}
          target="_blank"
          className="font-semibold text-brand-accent hover:underline"
        >
          PowerOn Pro
        </Link>
        .
        <br />
        <br />
        Now, we&apos;re laying the groundwork for a new place to manage our apps
        and even your credit union core. And, we&apos;re calling it Libum
        Portal!
      </p>
    ),
    groups: [
      {
        id: "portal",
        title: "A new place for your credit union",
        details: [
          {
            id: "1",
            node: (
              <p>
                <span className="font-bold">
                  Better UX, and an even better UI.
                </span>{" "}
                These aren&apos;t just buzzwords we casually throw around. Our
                focus is to create a place that&apos;s enjoyable to work with
                and easy to navigate. We actually want you to have fun while you
                work
                <span className="inline-flex px-1 align-middle">
                  <Image
                    src="/emojis/grinning-squinting-face.svg"
                    alt=""
                    width={18}
                    height={18}
                    className="mb-1"
                  />
                </span>
              </p>
            ),
          },
          {
            id: "2",
            node: (
              <p>
                <span className="font-bold">Continuous integration.</span>{" "}
                Having a portal gives us the freedom to efficiently design and
                develop powerful new features and ship them with the click of a
                button. What you see now is just the tip of the iceberg.
              </p>
            ),
          },
        ],
      },
      {
        id: "poweron-pro",
        title: "To manage Libum apps",
        details: [
          {
            id: "1",
            node: (
              <p>
                <span className="font-bold">Organization controls.</span> Take
                control of your organization&apos;s PowerOn Pro
                <sup className="font-bold text-brand-accent">+</sup>{" "}
                subscription and even guard against unwanted production changes
                <span className="inline-flex px-1 align-middle">
                  <Image
                    src="/emojis/rocket.svg"
                    alt=""
                    width={18}
                    height={18}
                    className="mb-1"
                  />
                </span>
              </p>
            ),
          },
          {
            id: "2",
            node: (
              <p>
                <span className="font-bold">Your NaaS, your way.</span> For the
                moment, you have to come to us for NexumAPI configuration
                changes. Soon, we&apos;ll give you the flexibility to do manage
                it yourself. Goodbye middleman.
              </p>
            ),
          },
        ],
      },
      {
        id: "future",
        title: "And maybe even your core",
        details: [
          {
            id: "1",
            node: (
              <p className="leading-tight">
                <span className="font-bold">Time for a new quest.</span> Guys,
                we&apos;re working on something pretty special — we just had to
                build a place for it first
                <span className="inline-flex px-1 align-middle">
                  <Image
                    src="/emojis/sparkles.svg"
                    alt=""
                    width={18}
                    height={18}
                    className="mb-1"
                  />
                </span>
                If you like old stuff on new UIs, this might be for you.
              </p>
            ),
          },
        ],
      },
    ],
    closer: (
      <>
        <p className="pb-4">
          It&apos;s our mission to bring the greatest software to your credit
          union. To do this, we know we have to listen to the community.
        </p>
        <p className="block">
          Let us know what you want on our{" "}
          <Link
            href={LIBUM_FEEDBACK_URL}
            target="_blank"
            className="font-semibold text-brand-accent hover:underline"
          >
            Feedback
          </Link>{" "}
          portal!
          <span className="inline-flex px-1 align-middle">
            <Image
              src="/emojis/cat-bug-love.webp"
              alt="A cat that loves like a bug"
              height={96}
              width={84}
              style={{ height: "20px", width: "auto" }}
              className="mb-1"
            />
          </span>
          We promise we listen.
        </p>
      </>
    ),
  },
];

export function useUpdates() {
  const [selectedUpdateIndex, setSelectedUpdateIndex] = useState(0);
  const [hasUnreadUpdates, setHasUnreadUpdates] = useState(false);
  const [shouldAutoShow, setShouldAutoShow] = useState(false);

  const update = UPDATES[selectedUpdateIndex];

  useEffect(() => {
    const lastViewedUpdate = getCookie(COOKIE_KEY);

    if (!lastViewedUpdate || lastViewedUpdate !== UPDATES[0].id) {
      setHasUnreadUpdates(true);
      // Only auto-show once per page session
      if (!hasAutoShownThisSession) {
        setShouldAutoShow(true);
        hasAutoShownThisSession = true;
      }
    }
  }, []);

  const markUpdatesAsRead = useCallback(() => {
    setCookie(COOKIE_KEY, UPDATES[0].id, { maxAge: ONE_YEAR_IN_SECONDS });
    setHasUnreadUpdates(false);
    setShouldAutoShow(false);
  }, []);

  const selectUpdate = (index: number) => {
    setSelectedUpdateIndex(index);
  };

  return {
    update,
    updates: UPDATES,
    selectedUpdateIndex,
    hasUnreadUpdates,
    shouldAutoShow,
    markUpdatesAsRead,
    selectUpdate,
  };
}
